/**
 * Pessoas: as contas do painel, e com qual pessoa do notebook cada uma entra.
 *
 * **A coluna "entra como" é a razão de esta tela existir.** Em 30/09/2026 a conta do Cristiano
 * estava ligada ao "Pastor Setorial" em vez de ao "Pastor do Campo". A senha certa era recusada,
 * o sintoma era idêntico ao de senha errada, e não havia tela nenhuma em que isso aparecesse.
 * Custou um dia inteiro de caça.
 *
 * **Quase tudo aqui é só leitura.** Criar conta e religar conta continuam no comando de
 * administração, com o token, até a etapa 3 da parte 4. Enquanto as ações que entregam o Campo
 * não exigirem o segundo fator, abrir uma tela que cria acesso ao dinheiro de um Campo inteiro
 * na internet seria abrir mais porta do que se tem fechadura.
 *
 * **A única ação é desligar o segundo fator de alguém**, e ela é do administrador máximo. É a
 * porta de emergência do item B do plano da parte 4: quem perde o celular e os dez códigos de
 * emergência no mesmo ano não tem outro caminho de volta.
 */
import { useEffect, useState, type JSX } from 'react'
import {
  ehSessaoTerminada, ErroDoServidor, servidor,
  type ComoNoNotebook, type Eu, type PessoaDoPainelNaTela, type QuemPodeTerConta,
  type TelaDePessoas
} from '../api'
import { AbasDaAdministracao } from '../componentes/AbasDaAdministracao'
import { CaixaDoCodigo } from '../componentes/CaixaDoCodigo'
import { BarraDeCima } from '../componentes/BarraDeCima'
import { quandoFoi } from '../dinheiro'

/** A cor da etiqueta de cada caso, reaproveitando as situações do mês. */
const ETIQUETA: Record<ComoNoNotebook, string> = {
  ATIVA: 'FECHADO',
  NAO_CHEGOU: 'NAO_FECHOU',
  DESATIVADA: 'NAO_FECHOU',
  EXCLUIDA: 'NAO_FECHOU'
}

const COMO_SE_DIZ: Record<ComoNoNotebook, string> = {
  ATIVA: 'ativa no notebook',
  NAO_CHEGOU: 'não chegou do notebook',
  DESATIVADA: 'desativada no notebook',
  EXCLUIDA: 'excluída no notebook'
}

export function Pessoas({ eu, aoSair }: { eu: Eu; aoSair: () => void }): JSX.Element {
  const [dados, setDados] = useState<TelaDePessoas | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [desligando, setDesligando] = useState<PessoaDoPainelNaTela | null>(null)
  const [criando, setCriando] = useState(false)
  const [feito, setFeito] = useState<string | null>(null)

  function recarregar(): void {
    servidor
      .pessoas()
      .then(setDados)
      .catch((falha: unknown) => {
        if (ehSessaoTerminada(falha)) return aoSair()
        setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível carregar.')
      })
  }

  useEffect(() => {
    let vale = true
    servidor
      .pessoas()
      .then((recebido) => {
        if (vale) setDados(recebido)
      })
      .catch((falha: unknown) => {
        if (!vale) return
        if (ehSessaoTerminada(falha)) return aoSair()
        setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível carregar.')
      })
    return () => {
      vale = false
    }
  }, [aoSair])

  return (
    <>
      <BarraDeCima eu={eu} aoSair={aoSair} voltarPara={{ endereco: '/', texto: 'Início' }} />

      <main className="folha" data-pronto={dados !== null ? 'sim' : erro !== null ? 'erro' : 'nao'}>
        <h1>Pessoas do painel</h1>
        <AbasDaAdministracao aquiEstou="/pessoas" />

        {erro !== null && <p className="recado" role="alert">{erro}</p>}
        {dados === null && erro === null && <p className="recado aguarde">Carregando...</p>}

        {dados !== null && (
          <>
            {/*
              O aviso do segundo fator fica no ALTO, e sempre. É na hora de pensar na décima
              conta que alguém precisa lembrar dele, e não no fim de uma lista comprida.
            */}
            <p className="cartao">
              <span className="titulo">O painel é para poucos, por enquanto</span>
              <span className="detalhe">{dados.aviso_do_segundo_fator}</span>
            </p>

            {feito !== null && <p className="recado feito" role="status">{feito}</p>}

            {eu.e_admin_maximo && !criando && (
              <button className="principal" type="button" onClick={() => { setFeito(null); setCriando(true) }}>
                Criar uma conta do painel
              </button>
            )}

            {criando && (
              <CriarConta
                candidatos={dados.quem_pode_ter_conta}
                aoFechar={() => setCriando(false)}
                aoCriar={(recado) => { setCriando(false); setFeito(recado); recarregar() }}
                aoSair={aoSair}
              />
            )}

            <ul className="pessoas-do-painel">
              {dados.pessoas.map((p) => (
                <li key={p.id} className={p.ativo ? undefined : 'cortado'}>
                  <span className="nome">
                    {p.nome}
                    {!p.ativo && <span className="situacao NAO_FECHOU">conta desativada</span>}
                    {p.bloqueada_ate !== null && (
                      <span className="situacao TRAVADO">bloqueada por erros de senha</span>
                    )}
                  </span>
                  <span className="detalhe">{p.email}</span>

                  {/*
                    A linha que custou o dia 30/09: com qual pessoa do notebook a conta entra, e
                    pelo nome, como ele aparece na lista de pessoas do notebook.
                  */}
                  <span className="entra-como">
                    {p.no_notebook === null ? (
                      <>Não está ligada a ninguém do notebook, e por isso não alcança nada.</>
                    ) : (
                      <>
                        Entra como <strong>{p.no_notebook.nome ?? 'pessoa que o servidor não achou'}</strong>
                        <span className={`situacao ${ETIQUETA[p.no_notebook.como]}`}>
                          {COMO_SE_DIZ[p.no_notebook.como]}
                        </span>
                        {p.no_notebook.congregacao !== null && <> · {p.no_notebook.congregacao}</>}
                        {p.no_notebook.e_admin_maximo && <> · administrador máximo</>}
                      </>
                    )}
                  </span>

                  {p.no_notebook !== null && p.no_notebook.como !== 'ATIVA' && (
                    <span className="detalhe atencao">{p.no_notebook.explicacao}</span>
                  )}

                  <span className="detalhe">
                    Alcança: {p.alcance} · último acesso: {quandoFoi(p.ultimo_acesso, 'nunca entrou')}
                  </span>

                  {/*
                    O segundo fator de cada conta, com a mesma etiqueta das outras situações:
                    quem administra precisa ver, de uma olhada, quantas contas estão protegidas
                    por só uma senha.
                  */}
                  <span className="detalhe">
                    Segundo fator:{' '}
                    <span className={`situacao ${p.tem_segundo_fator ? 'FECHADO' : 'NAO_FECHOU'}`}>
                      {p.tem_segundo_fator ? 'ligado' : 'não tem'}
                    </span>
                    {p.tem_segundo_fator && eu.e_admin_maximo && (
                      <>
                        {' '}
                        <button className="como-link" type="button" onClick={() => setDesligando(p)}>
                          desligar
                        </button>
                      </>
                    )}
                  </span>
                </li>
              ))}
            </ul>

            <p className="explicacao">
              Toda conta criada aqui entra com a senha do notebook e é obrigada a ligar o
              segundo fator no primeiro acesso, antes de ver qualquer coisa. Religar uma conta a
              outra pessoa do notebook continua no comando de administração: é conserto raro, e
              cada tela a mais na internet é uma porta a mais para cuidar.
            </p>

            {eu.e_admin_maximo && <EnvioDeEmail servico={dados.envio_de_email.servico} />}
          </>
        )}

        {desligando !== null && (
          <DesligarOSegundoFator
            pessoa={desligando}
            aoFechar={() => setDesligando(null)}
            aoDesligar={() => {
              setDesligando(null)
              recarregar()
            }}
            aoSair={aoSair}
          />
        )}
      </main>
    </>
  )
}

/**
 * O envio de e-mail: qual serviço está no ar, e o botão de provar que ele chega.
 *
 * **Por que este botão existe.** O envio tem três partes que só falham de verdade em produção: a
 * chave do serviço, os registros de DNS do remetente e a reputação do subdomínio. Prova
 * automática nenhuma alcança essas três. Quem configurou precisa apertar um botão e olhar a
 * própria caixa de entrada, e precisa disso ANTES de uma pessoa de verdade depender do envio
 * para voltar a entrar no sistema.
 *
 * **O nome do serviço aparece antes de apertar, e é a informação mais útil daqui.** Enquanto
 * estiver escrito `arquivo`, o e-mail é gravado num arquivo dentro do servidor e não sai: quem
 * apertasse e não recebesse nada ia culpar o spam do provedor dele, e procurar no lugar errado.
 *
 * O e-mail chega na caixa de quem apertou, e não leva nada dentro. Nem senha, nem link, nem
 * nome: ele serve para dizer "chegou".
 */
function EnvioDeEmail({ servico }: { servico: string }): JSX.Element {
  const [mandando, setMandando] = useState(false)
  const [feito, setFeito] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  const estaConfigurado = servico !== 'arquivo'

  async function mandar(): Promise<void> {
    setErro(null)
    setFeito(null)
    setMandando(true)
    try {
      const posto = await servidor.mandarEmailDeTeste()
      setFeito(
        `O aviso entrou na fila e vai sair pelo serviço "${posto.servico}". Olhe a sua caixa de ` +
        'entrada em alguns minutos, e a de spam também.'
      )
    } catch (falha) {
      setErro(
        falha instanceof ErroDoServidor
          ? falha.message
          : 'Não foi possível pedir o teste. Tente de novo.'
      )
    } finally {
      setMandando(false)
    }
  }

  return (
    <section className="cartao">
      <span className="titulo">O envio de e-mail</span>

      <span className="detalhe">
        Serviço no ar:{' '}
        <span className={`situacao ${estaConfigurado ? 'FECHADO' : 'NAO_FECHOU'}`}>{servico}</span>
        {!estaConfigurado && (
          <>
            {' '}
            O e-mail está sendo gravado em arquivo dentro do servidor e <strong>não sai</strong>.
            Enquanto estiver assim, ninguém recebe link de trocar a senha.
          </>
        )}
      </span>

      {feito !== null && <p className="recado feito" role="status">{feito}</p>}
      {erro !== null && <p className="recado" role="alert">{erro}</p>}

      <button type="button" onClick={() => void mandar()} disabled={mandando}>
        {mandando ? 'Mandando...' : 'Mandar um e-mail de teste para mim'}
      </button>

      <span className="detalhe">
        Chega na sua caixa, e não leva nada dentro: nem nome, nem senha, nem link. Ele serve para
        uma coisa só, provar que o e-mail deste sistema chega na caixa de entrada.
      </span>
    </section>
  )
}

/**
 * A porta de emergência: o administrador máximo desliga o segundo fator de alguém.
 *
 * Ela pede **motivo escrito e a senha de novo**, como cortar um notebook pede. O motivo fica na
 * trilha, que não se apaga, porque esta é a ação que tira a proteção de uma conta que vê o
 * Campo inteiro, e um dia alguém vai querer saber quem mandou e por quê.
 */
function DesligarOSegundoFator({
  pessoa,
  aoFechar,
  aoDesligar,
  aoSair
}: {
  pessoa: PessoaDoPainelNaTela
  aoFechar: () => void
  aoDesligar: () => void
  aoSair: () => void
}): JSX.Element {
  const [motivo, setMotivo] = useState('')
  const [senha, setSenha] = useState('')
  const [codigo, setCodigo] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  async function enviar(evento: React.FormEvent): Promise<void> {
    evento.preventDefault()
    setErro(null)
    setEnviando(true)
    try {
      await servidor.desligarOSegundoFatorDe(pessoa.id, motivo.trim(), senha, codigo.trim())
      aoDesligar()
    } catch (falha) {
      if (ehSessaoTerminada(falha)) return aoSair()
      setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível desligar.')
      setSenha('')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <section className="confirmar-reabrir" role="dialog" aria-label="Desligar o segundo fator">
      <h2>Desligar o segundo fator de {pessoa.nome}</h2>

      <p className="explicacao atencao">
        Depois disto, a conta dela volta a entrar só com a senha, até ela ligar outro. Todas as
        sessões abertas dela caem agora.
      </p>
      <p className="explicacao">
        Faça isto quando a pessoa perdeu o celular <strong>e</strong> os dez códigos de
        emergência. Se ela ainda tem um código, ela mesma entra e troca de celular.
      </p>

      {erro !== null && <p className="recado" role="alert">{erro}</p>}

      <form onSubmit={enviar}>
        <label className="campo">
          Por que você está desligando?
          <input
            type="text"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            maxLength={500}
            minLength={3}
            required
          />
        </label>

        <label className="campo">
          Sua senha
          <input
            type="password"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>

        <CaixaDoCodigo valor={codigo} aoMudar={setCodigo} desabilitada={enviando} />

        <button
          className="principal perigo"
          type="submit"
          disabled={enviando || codigo.trim().length < 6}
        >
          {enviando ? 'Desligando...' : 'Desligar o segundo fator'}
        </button>
        <button type="button" className="discreto" onClick={aoFechar}>
          Deixar como está
        </button>
      </form>
    </section>
  )
}

/**
 * Criar uma conta do painel, pela tela. É a etapa 3 da parte 4.
 *
 * **A pessoa do notebook é escolhida numa lista, e não digitada.** Digitar um id seria repetir
 * o erro do dia 30/09/2026 com outra roupa: a conta do Cristiano estava ligada ao "Pastor
 * Setorial" em vez de ao "Pastor do Campo", e ninguém tinha como ver. Aqui ele escolhe pelo
 * NOME, como ele aparece na lista de pessoas do notebook, com a congregação ao lado.
 *
 * A senha não aparece em lugar nenhum deste formulário, e isso é de propósito: quem entra pelo
 * painel entra com a senha que já usa no notebook. Ninguém cria senha para ninguém aqui.
 */
function CriarConta({
  candidatos,
  aoFechar,
  aoCriar,
  aoSair
}: {
  candidatos: QuemPodeTerConta[]
  aoFechar: () => void
  aoCriar: (recado: string) => void
  aoSair: () => void
}): JSX.Element {
  const [usuarioId, setUsuarioId] = useState(candidatos[0]?.usuario_id ?? '')
  /**
   * O e-mail vem preenchido com o que a pessoa já tem no notebook, quando ela tem.
   *
   * **Isto é para o endereço não ser digitado duas vezes, e as duas diferentes.** São dois campos
   * independentes, e nada os obriga a bater; mas quando eles batem, quem esquecer a senha acha o
   * caminho de volta digitando qualquer um dos dois, porque o servidor procura nos dois lugares.
   * Quem quiser um endereço diferente aqui apaga e digita outro.
   */
  const [email, setEmail] = useState(candidatos[0]?.email ?? '')
  const [senha, setSenha] = useState('')
  const [codigo, setCodigo] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  const escolhido = candidatos.find((c) => c.usuario_id === usuarioId)

  async function enviar(evento: React.FormEvent): Promise<void> {
    evento.preventDefault()
    setErro(null)
    setEnviando(true)
    try {
      const criada = await servidor.criarConta({
        email: email.trim(),
        // O nome da conta é o nome da pessoa no notebook: duas grafias para a mesma pessoa
        // seriam duas pessoas na hora de alguém procurar.
        nome: escolhido?.nome ?? '',
        usuario_id: usuarioId,
        senha,
        codigo: codigo.trim()
      })
      aoCriar(
        `Conta criada para ${criada.entra_como}. Ela entra com a senha do notebook e vai ter ` +
        'que ligar o segundo fator no primeiro acesso.'
      )
    } catch (falha) {
      if (ehSessaoTerminada(falha)) return aoSair()
      setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível criar a conta.')
      setSenha('')
      setCodigo('')
    } finally {
      setEnviando(false)
    }
  }

  if (candidatos.length === 0) {
    return (
      <section className="confirmar-reabrir" role="dialog" aria-label="Criar conta do painel">
        <h2>Criar uma conta do painel</h2>
        <p className="explicacao">
          Todo mundo que está ativo no notebook já tem conta. Para dar painel a alguém novo,
          cadastre a pessoa no notebook primeiro e espere o notebook dela sincronizar.
        </p>
        <button type="button" className="discreto" onClick={aoFechar}>Fechar</button>
      </section>
    )
  }

  return (
    <section className="confirmar-reabrir" role="dialog" aria-label="Criar conta do painel">
      <h2>Criar uma conta do painel</h2>

      <p className="explicacao atencao">
        Quem entrar com esta conta vê o dinheiro que a grade do notebook deixar ela ver. Não
        crie conta para quem trabalha só na tesouraria: tesoureiro de congregação trabalha no
        notebook e não precisa de painel.
      </p>

      <form onSubmit={enviar}>
        <label>
          Quem do notebook vai entrar?
          <select
            className="campo"
            value={usuarioId}
            onChange={(e) => {
              setUsuarioId(e.target.value)
              /*
               * Trocou de pessoa, troca o e-mail: o endereço é DELA, e deixar na tela o da
               * pessoa anterior seria criar a conta com o e-mail de outro, que é o erro mais
               * caro que esta tela consegue cometer.
               */
              setEmail(candidatos.find((c) => c.usuario_id === e.target.value)?.email ?? '')
            }}
            required
          >
            {candidatos.map((c) => (
              <option key={c.usuario_id} value={c.usuario_id}>
                {c.nome}
                {c.congregacao === null ? '' : ` · ${c.congregacao}`}
                {c.e_admin_maximo ? ' · administrador máximo' : ''}
              </option>
            ))}
          </select>
          <span className="ajuda-do-codigo">
            O nome é o mesmo da lista de pessoas do notebook. A senha e as permissões saem de
            lá, e continuam saindo: mexer na grade lá muda o que esta conta vê aqui.
          </span>
        </label>

        <label>
          E-mail de quem vai entrar
          <input
            className="campo"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoCapitalize="none"
            spellCheck={false}
            maxLength={200}
            required
          />
          <span className="ajuda-do-codigo">
            {escolhido?.email === null || escolhido?.email === undefined ? (
              <>
                Esta pessoa não tem e-mail no cadastro do notebook. Digite o endereço dela aqui, e
                vale anotar no notebook também: é por ele que ela recupera a senha se esquecer.
              </>
            ) : (
              <>
                Já veio preenchido com o e-mail do cadastro do notebook. É só o endereço da
                porta: a senha continua sendo a do notebook.
              </>
            )}
          </span>
        </label>

        <label>
          Sua senha
          <input
            className="campo"
            type="password"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>

        <CaixaDoCodigo valor={codigo} aoMudar={setCodigo} desabilitada={enviando} />

        {erro !== null && <p className="recado" role="alert">{erro}</p>}

        <div className="linha-form">
          <button
            type="submit"
            disabled={enviando || email.trim() === '' || senha === '' || codigo.trim().length < 6}
          >
            {enviando ? 'Criando...' : 'Criar a conta'}
          </button>
          <button type="button" onClick={aoFechar} disabled={enviando}>
            Desistir
          </button>
        </div>
      </form>
    </section>
  )
}
